/**
 * BhaPanel — the BHA/Bit body: KPI band, the BHA-runs table (run #, in/out MD,
 * hours, bit, ROP, components), and — for the selected run — its detail (bottom-
 * up components, bit facts, dull grade, bit-ops grid) beside the to-scale BHA +
 * PDC-bit schematic, with an ROP-vs-depth strip below. Every computed number
 * (ROP, hours) is served by Oxbo-P; the FE formats + converts units only.
 */
import { useCallback, useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventBhaRunsQuery } from "@api/pApi";
import { useGetCatalogQuery } from "@api/mApi";
import { useUnits } from "@units/useUnits";
import { fmtNum, fmtHours } from "@lib/format";
import type { BhaRunWithChildren, CatalogValue } from "@api/types";
import { inchFrac } from "./inchFrac";
import { RunDetail } from "./RunDetail";
import { BhaSchematic } from "./BhaSchematic";
import { RopChart } from "./RopChart";
import { NewRunModal, type NewRunDraft } from "./NewRunModal";
import "./bha.css";

interface Props {
  eventId: string;
  /** Stored unit profile of the event (measurements are in these units). */
  storedProfile?: string | null;
  /** Read-only when the underlying report is closed. */
  readOnly?: boolean;
}

/** Build a code→label lookup over a catalog's values (empty until loaded). */
function useLabeler(catalogKey: string) {
  const { data } = useGetCatalogQuery({ key: catalogKey });
  const map = useMemo(() => {
    const m = new Map<string, CatalogValue>();
    (data?.values ?? []).forEach((v) => m.set(v.code, v));
    return m;
  }, [data]);
  return (code: string | null | undefined) => (code ? (map.get(code)?.label ?? code) : "—");
}

export function BhaPanel({ eventId, storedProfile, readOnly }: Props) {
  const { t, locale } = useLocale();
  const units = useUnits(storedProfile);
  const vendorLabel = useLabeler("vendors");
  const typeLabelFn = useLabeler("component_types");
  const typeLabel = (code: string) => typeLabelFn(code);

  const { data, isLoading, isError, refetch } = useGetEventBhaRunsQuery(eventId, {
    skip: !eventId,
  });

  // Optimistically-added runs live alongside the served ones until the POST
  // wiring lands (§10.3). Keyed separately so a refetch never drops them.
  const [added, setAdded] = useState<BhaRunWithChildren[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const runs = useMemo<BhaRunWithChildren[]>(
    () => [...(Array.isArray(data?.runs) ? data.runs : []), ...added].sort((a, b) => b.run_no - a.run_no),
    [data, added],
  );

  // Default selection: the in-hole run, else the highest-numbered run.
  const selected = useMemo(() => {
    if (selectedId) {
      const hit = runs.find((r) => r.id === selectedId);
      if (hit) return hit;
    }
    return runs.find((r) => r.in_hole) ?? runs[0] ?? null;
  }, [runs, selectedId]);

  const depth = useCallback(
    (v: number | null) => units.fmt(units.convertField("assembly", "md_top", v, storedProfile)),
    [units, storedProfile],
  );
  // Axis unit symbols resolved from the display profile (ft/m, ft/h / m/h).
  const depthUnit = units.convertField("assembly", "md_top", 0, storedProfile).unit || "ft";
  const ropUnit = units.convertField("daily_report", "rop_avg_day", 0, storedProfile).unit || "ft/h";

  const stats = useMemo<Stat[]>(
    () => bhaKpis(runs, t, locale, depth, ropUnit),
    [runs, t, locale, depth, ropUnit],
  );

  const nextRunNo = runs.reduce((mx, r) => Math.max(mx, r.run_no), 0) + 1;

  const onSave = (draft: NewRunDraft) => {
    if (draft.run_no == null) return;
    const id = `bha-local-${draft.run_no}`;
    const run: BhaRunWithChildren = {
      id,
      event_id: eventId,
      well_uwi: data?.runs[0]?.well_uwi ?? "",
      run_no: draft.run_no,
      md_in: draft.md_in,
      md_out: null,
      hours: null,
      rop_avg: null,
      bit_size_entry_key: draft.bit_size_entry_key,
      bit_iadc: null,
      bit_jets: draft.bit_jets,
      bit_tfa: null,
      bit_type_entry_key: draft.bit_type_entry_key,
      bit_vendor_entry_key: draft.bit_vendor_entry_key,
      dull_grade: null,
      pull_reason: null,
      in_hole: true,
      carry_over: true,
      assembly: [],
      bit_ops: [],
    };
    setAdded((a) => [...a, run]);
    setSelectedId(id);
  };

  return (
    <div className="bha-screen">
      <div className="panel-head-row">
        <div />
        {!readOnly ? (
          <button type="button" className="btn primary" onClick={() => setModalOpen(true)}>
            + {t("bha.newRun")}
          </button>
        ) : null}
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={runs.length === 0}
        emptyMessage={t("bha.noData")}
        onRetry={refetch}
      >
        {stats.length ? <StatBand stats={stats} /> : null}

        {/* runs table */}
        <div className="card" style={{ marginTop: 14 }}>
          <h3>{t("bha.runs")}</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("bha.col.run")}</th>
                  <th className="num">{t("bha.col.inMd")}</th>
                  <th className="num">{t("bha.col.outMd")}</th>
                  <th className="num">{t("bha.col.hours")}</th>
                  <th>{t("bha.col.bit")}</th>
                  <th className="num">{t("bha.col.rop")}</th>
                  <th>{t("bha.col.components")}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <RunRow
                    key={r.id}
                    run={r}
                    selected={selected?.id === r.id}
                    onSelect={() => setSelectedId(r.id)}
                    depth={depth}
                    locale={locale}
                    inHoleLabel={t("bha.inHole")}
                    componentsSummary={componentsSummary(r, typeLabel)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* selected-run detail + schematic */}
        {selected ? (
          <>
            <div className="bha-detail-grid">
              <RunDetail
                run={selected}
                units={units}
                storedProfile={storedProfile}
                vendorLabel={(c) => vendorLabel(c)}
                typeLabel={typeLabel}
              />
              <div className="card" style={{ padding: 16 }}>
                <h3>{t("bha.schematic")}</h3>
                {selected.assembly?.length ? (
                  <BhaSchematic
                    assembly={selected.assembly}
                    vendorLabel={(c) => vendorLabel(c)}
                    typeLabel={typeLabel}
                    bitMd={selected.md_out ?? selected.md_in}
                    fmtDepth={depth}
                    ariaLabel={`BHA schematic for run ${selected.run_no}`}
                  />
                ) : (
                  <div className="empty-state">{t("bha.selectRun")}</div>
                )}
              </div>
            </div>

            {selected.bit_ops?.length ? (
              <div style={{ marginTop: 14 }}>
                <RopChart
                  ops={selected.bit_ops}
                  depthUnit={depthUnit}
                  ropUnit={ropUnit}
                  subtitle={`${depth(selected.md_in)} – ${depth(selected.md_out ?? selected.md_in)}`}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </QueryState>

      <NewRunModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={onSave}
        nextRunNo={nextRunNo}
        context={`${t("bha.col.run")} ${nextRunNo}`}
      />
    </div>
  );
}

/** One row of the runs table. */
function RunRow({
  run,
  selected,
  onSelect,
  depth,
  locale,
  inHoleLabel,
  componentsSummary,
}: {
  run: BhaRunWithChildren;
  selected: boolean;
  onSelect: () => void;
  depth: (v: number | null) => string;
  locale: Parameters<typeof fmtNum>[1];
  inHoleLabel: string;
  componentsSummary: string;
}) {
  return (
    <tr
      className={`run-row${selected ? " selected" : ""}${run.carry_over ? " carried" : ""}`}
      data-owner="p"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <td>
        {run.run_no}
        {run.in_hole ? (
          <span className="badge" title={inHoleLabel} style={{ marginLeft: 6 }}>
            {inHoleLabel}
          </span>
        ) : null}
      </td>
      <td className="num tab-nums">{depth(run.md_in)}</td>
      <td className="num tab-nums">{run.md_out == null ? "—" : depth(run.md_out)}</td>
      <td className="num tab-nums">{fmtNum(run.hours, locale, { maximumFractionDigits: 1 })}</td>
      <td>
        {inchFrac(run.bit_size_entry_key)}
        {run.bit_iadc ? ` · ${run.bit_iadc}` : ""}
      </td>
      <td className="num tab-nums">{fmtNum(run.rop_avg, locale, { maximumFractionDigits: 1 })}</td>
      <td className="dim">{componentsSummary}</td>
    </tr>
  );
}

/** A short "3 stab + collars" summary of a run's assembly for the table cell. */
function componentsSummary(run: BhaRunWithChildren, typeLabel: (c: string) => string): string {
  const assembly = run.assembly ?? [];
  const parts = assembly
    .filter((c) => c.component_type_entry_key.toUpperCase() !== "BIT")
    .slice(0, 3)
    .map((c) => typeLabel(c.component_type_entry_key));
  if (parts.length === 0) return "—";
  const extra = assembly.length - 1 - parts.length;
  return extra > 0 ? `${parts.join(" · ")} +${extra}` : parts.join(" · ");
}

/** KPI band: on-bottom ROP + drilled interval + bit hours + last dull grade. */
function bhaKpis(
  runs: BhaRunWithChildren[],
  t: ReturnType<typeof useLocale>["t"],
  locale: Parameters<typeof fmtNum>[1],
  depth: (v: number | null) => string,
  ropUnit: string,
): Stat[] {
  const current = runs.find((r) => r.in_hole) ?? runs[0];
  if (!current) return [];
  const graded = runs.find((r) => r.dull_grade);
  const interval =
    current.md_in != null && current.md_out != null ? current.md_out - current.md_in : null;
  return [
    {
      label: t("bha.kpi.rop"),
      value: fmtNum(current.rop_avg, locale, { maximumFractionDigits: 0 }),
      sub: ropUnit,
      tone: "good",
    },
    { label: t("bha.kpi.interval"), value: interval == null ? "—" : depth(interval) },
    { label: t("bha.kpi.bitHours"), value: fmtHours(current.hours, locale), tone: "ops" },
    {
      label: t("bha.kpi.lastDull"),
      value: graded?.dull_grade ?? "—",
      tone: graded ? "warn" : undefined,
    },
  ];
}
